import { useEffect } from 'react';
import { getEligibleOfferFromStorage } from '../../../components/instant-offers/utils/instantOffersStorage';
import { trackEDDL, ANALYTICS_CONSTANTS } from '../../../utilities/analytics';
import { AppointmentTypes } from './constants';
import useAppState from '../../../common/state/appState';
import { IQuickLinksData } from '../../../types/quickLinks';

const convertStatusToText = (status: boolean | null | undefined): string => {
    if (status === undefined || status === null) {
        return 'undefined';
    }
    return status ? 'active' : 'inactive';
};

const convertTransferStatusToText = (transferStatus: string | undefined, isTestDrive: boolean | undefined): string => {
    if (transferStatus === undefined || transferStatus === '') {
        return 'undefined';
    }
    return !isTestDrive ? 'active' : 'inactive';
};

const checkIfTestDrive = (
    transferStatus: { status: string; appointmentType?: string } | undefined,
): boolean | undefined => {
    if (!transferStatus?.status) {
        return undefined;
    }

    const { appointmentType } = transferStatus;
    if (!appointmentType) {
        return false;
    }

    return appointmentType.toLowerCase().includes(AppointmentTypes.TEST_DRIVE.toLowerCase());
};
interface IConvertQuickLinksToTagParams {
    quickLinksLoaded: boolean;
    quickLinks?: IQuickLinksData;
}

const convertQuickLinksToTag = ({ quickLinksLoaded, quickLinks }: IConvertQuickLinksToTagParams): string => {
    if (!quickLinksLoaded || !quickLinks || quickLinks.prompts.length === 0) {
        return 'undefined:undefined';
    }

    const hasRecentSearch = quickLinks.prompts.some((p) => p.queryKey === 'recent_search');
    const hasHighlight = quickLinks.prompts.some((p) => p.highlight);
    return `${hasRecentSearch}:${hasHighlight}`;
};

const usePageStateTracking = () => {
    const isPrequalified = useAppState((state) => state.isPrequalified);
    const eligibleOffer = getEligibleOfferFromStorage();
    const instantOfferStatus = eligibleOffer === undefined ? undefined : !!eligibleOffer;
    const transferStatus = useAppState((state) => state.transferStatus);
    const quickLinksLoaded = useAppState((state) => state.quickLinksLoaded);
    const quickLinks = useAppState((state) => state.quickLinks);
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const pqStatus = convertStatusToText(isPrequalified);
            const ioStatus = convertStatusToText(instantOfferStatus);

            const quickLinksTag = convertQuickLinksToTag({ quickLinksLoaded, quickLinks });
            let pageStateString: string;

            if (transferStatus) {
                const isTestDriveStatus = checkIfTestDrive(transferStatus);
                const resStatus = convertStatusToText(isTestDriveStatus);
                const transferStatusValue = convertTransferStatusToText(transferStatus?.status, isTestDriveStatus);
                pageStateString = `hp-HeroHomebaseMVP-Viewed:${pqStatus} pq:${ioStatus} io:${resStatus} res:${transferStatusValue} transfer`;
            } else {
                pageStateString = `hp-HeroHomebaseMVP-Viewed:${pqStatus} pq:${ioStatus} io:inactive res:inactive transfer`;
            }
            trackEDDL({
                event: ANALYTICS_CONSTANTS.EVENTS.PAGE.STATE,
                pageState: { pageState: pageStateString, numOfNewCars: quickLinksTag },
            });
        }, ANALYTICS_CONSTANTS.PAGE_STATE_LOAD_TIME);

        return () => clearTimeout(timeoutId);
    }, [instantOfferStatus, isPrequalified, transferStatus, quickLinksLoaded, quickLinks]);
};

export default usePageStateTracking;
